import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import withResponsiveLayout from './withResponsiveLayout';
import OffersV4MobileLayout from './OffersV4MobileLayout';
import OffersV4DesktopLayout from './OffersV4DesktopLayout';
import OffersV4Confetti from './OffersV4Confetti';
import actionCreator from '../../redux/offer/action-creator';
import offerStateSelectors from '../../redux/offer/state-selectors';
import loanSelectors from '../../../../selectors/loan';
const { loanSelector } = loanSelectors;

const ResponsiveLayout = withResponsiveLayout(OffersV4MobileLayout, OffersV4DesktopLayout);

/**
 * How far below the both-terms ceiling the starting amount is anchored, and the minimum range
 * width for that step to apply (LPL-2655).
 */
const BOTH_TERMS_STEP_DOWN = 1000;

/**
 * The two terms the offers page compares.
 */
const TERM_36_MONTHS = 36;
const TERM_60_MONTHS = 60;

/**
 * The lowest (floor) and highest (ceiling) offer amounts at which both a 36- and 60-month offer
 * exist.
 *
 * @param {object[]} sortedOffers offers from state.offersPage.offers.sortedOffers
 * @returns {{floor: number, ceiling: number}|null} null when no amount offers both terms
 */
export function getBothTermsRange(sortedOffers) {
    const durationsByAmount = sortedOffers.reduce((acc, offer) => {
        if (offer.duration !== TERM_36_MONTHS && offer.duration !== TERM_60_MONTHS) return acc;
        acc[offer.amount] = acc[offer.amount] || new Set();
        acc[offer.amount].add(offer.duration);
        return acc;
    }, {});

    return Object.keys(durationsByAmount).reduce((range, key) => {
        const durations = durationsByAmount[key];
        if (!durations.has(TERM_36_MONTHS) || !durations.has(TERM_60_MONTHS)) return range;

        const amount = Number(key);
        if (!range) return { floor: amount, ceiling: amount };
        return {
            floor: Math.min(range.floor, amount),
            ceiling: Math.max(range.ceiling, amount),
        };
    }, null);
}

/**
 * Restores a previously selected offer when it still exists in sortedOffers. Otherwise defaults
 * to maxOffer, anchored $1,000 below the both-terms ceiling when the client's max sits above it,
 * or on the both-terms floor when that range is narrower than $1,000 (LPL-2655).
 *
 * @returns {{sliderAmount: number, selectedOfferId: ?string}} initial amount and restored
 *   selection, if any
 */
export function getDefaultOfferState({ selectedOffer, maxOffer, sortedOffers }) {
    if (selectedOffer && selectedOffer.amount && selectedOffer.duration) {
        const match = sortedOffers.find(o => o.amount === selectedOffer.amount && o.duration === selectedOffer.duration);
        if (match) {
            return { sliderAmount: match.amount, selectedOfferId: match.id };
        }
    }

    let sliderAmount = maxOffer;
    const bothTermsRange = getBothTermsRange(sortedOffers);
    if (bothTermsRange && maxOffer > bothTermsRange.ceiling) {
        const { floor, ceiling } = bothTermsRange;
        sliderAmount = (ceiling - floor) >= BOTH_TERMS_STEP_DOWN
            ? ceiling - BOTH_TERMS_STEP_DOWN
            : floor;
    }
    return { sliderAmount, selectedOfferId: null };
}

export class OffersV4LayoutComponent extends Component {
    static propTypes = {
        firstName: PropTypes.string,
        maxOffer: PropTypes.number.isRequired,
        minOffer: PropTypes.number,
        customizedOffer: PropTypes.number,
        sortedOffers: PropTypes.array,
        selectOffer: PropTypes.func.isRequired,
        isSelectingOffer: PropTypes.bool.isRequired,
        counterOffer: PropTypes.bool,
        counterOfferReportChanged: PropTypes.bool,
    };

    static defaultProps = {
        firstName: '',
        minOffer: 0,
        customizedOffer: 0,
        sortedOffers: [],
        isSelectingOffer: false,
        counterOffer: false,
        counterOfferReportChanged: false,
    };

    constructor(props) {
        super(props);
        this.hasUserSetAmount = false;
        const { sliderAmount, selectedOfferId } = getDefaultOfferState(props);
        this.state = {
            sliderAmount,
            selectedOfferId,
            isDialogOpen: false,
        };
    }

    /**
     * Re-derives the default when sortedOffers changes mid-session, unless the client already set
     * an amount themselves.
     */
    componentDidUpdate(prevProps) {
        if (this.hasUserSetAmount || prevProps.sortedOffers === this.props.sortedOffers) {
            return;
        }
        const { sliderAmount, selectedOfferId } = getDefaultOfferState(this.props);
        this.setState({ sliderAmount, selectedOfferId });
    }

    handleAmountChange = (amount) => {
        this.hasUserSetAmount = true;
        this.setState({ sliderAmount: amount, selectedOfferId: null });
    };

    handleEditClick = (e) => {
        this.openedViaKeyboard = e && e.detail === 0;
        this.triggerElement = document.activeElement;
        this.setState({ isDialogOpen: true });
    };

    handleDialogClose = () => {
        this.setState({ isDialogOpen: false });
        if (this.openedViaKeyboard && this.triggerElement && this.triggerElement.focus) {
            this.triggerElement.focus();
        }
    };

    handleDialogSubmit = (amount) => {
        this.handleAmountChange(amount);
        this.handleDialogClose();
    };

    handleCardSelect = (offerId) => {
        this.setState({ selectedOfferId: offerId });
    };

    handleContinue = () => {
        const { selectedOfferId } = this.state;
        if (selectedOfferId) {
            this.props.selectOffer(selectedOfferId);
        }
    };

    getOffersAtAmount(amount) {
        const { sortedOffers } = this.props;
        return sortedOffers
            .filter(offer => offer.amount === amount)
            .sort((a, b) => a.duration - b.duration);
    }

    getGuidanceTags(offersAtAmount) {
        if (offersAtAmount.length < 2) return {};

        let lowestAPR = offersAtAmount[0];
        let lowestPayment = offersAtAmount[0];

        for (let i = 1; i < offersAtAmount.length; i++) {
            if (offersAtAmount[i].annualPercentageRate < lowestAPR.annualPercentageRate) {
                lowestAPR = offersAtAmount[i];
            }
            if (offersAtAmount[i].monthlyPayment < lowestPayment.monthlyPayment) {
                lowestPayment = offersAtAmount[i];
            }
        }

        const tags = {};
        if (lowestAPR.id === lowestPayment.id) {
            tags[lowestAPR.id] = { label: 'Best Value', variant: 'success' };
        } else {
            tags[lowestAPR.id] = { label: 'Lowest APR', variant: 'information' };
            tags[lowestPayment.id] = { label: 'Lowest Monthly', variant: 'success' };
        }
        return tags;
    }

    render() {
        const { maxOffer, minOffer, isSelectingOffer, counterOffer, counterOfferReportChanged } = this.props;
        const { sliderAmount, selectedOfferId, isDialogOpen } = this.state;

        const offersAtAmount = this.getOffersAtAmount(sliderAmount);
        const guidanceTags = this.getGuidanceTags(offersAtAmount);

        return (
            <React.Fragment>
            <OffersV4Confetti />
            <ResponsiveLayout
                maxOffer={maxOffer}
                minOffer={minOffer}
                sliderAmount={sliderAmount}
                selectedOfferId={selectedOfferId}
                isSelectingOffer={isSelectingOffer}
                offersAtAmount={offersAtAmount}
                guidanceTags={guidanceTags}
                isDialogOpen={isDialogOpen}
                counterOffer={counterOffer}
                counterOfferReportChanged={counterOfferReportChanged}
                onAmountChange={this.handleAmountChange}
                onCardSelect={this.handleCardSelect}
                onContinue={this.handleContinue}
                onEditClick={this.handleEditClick}
                onDialogClose={this.handleDialogClose}
                onDialogSubmit={this.handleDialogSubmit}
            />
            </React.Fragment>
        );
    }
}

const mapStateToProps = (state, props) => {
    const { offersPage = {} } = state;
    const { offers = {} } = offersPage;
    const { sortedOffers = [] } = offers;
    const isSelectingOffer = offerStateSelectors.getOfferSelecting(state);
    const selectedOffer = loanSelector.getSelectedOffer(state);

    return {
        ...props,
        sortedOffers,
        selectedOffer,
        isSelectingOffer: isSelectingOffer ? true : false,
    };
};

const mapDispatchToProps = (dispatch) => ({
    selectOffer: (offerId) => dispatch((_, getState) => {
        const state = getState();
        if (state && state.loan && state.loan.loanJson && state.loan.loanJson.loanId) {
            dispatch(actionCreator.selectOfferAuthenticated(offerId, state.loan.loanJson.loanId));
        } else {
            dispatch(actionCreator.selectOffer(offerId));
        }
    }),
});

export const OffersV4Layout = connect(
    mapStateToProps,
    mapDispatchToProps
)(OffersV4LayoutComponent);

export default OffersV4Layout;
